import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { PageUrls } from '@constants/page-urls.constants';
import { calculateBulkOrderItems } from '@models/functions.model';
import { NotificationResponse } from '@models/notifications.model';
import { ExistingAssetDetails, OrderDetailsSummary } from '@models/shopping-cart.model';
import { TranslationPipe } from '@pipes/translation/translation.pipe';
import { ShoppingService } from '@services/shopping/shopping.service';
import { TranslationService } from '@services/translation/translation.service';
import { Subscription } from 'rxjs';
import { DataLoaderCaptureOverrideDirective } from 'tangoe-design-system';

import { OrderDetailsComponent } from '../order-details/order-details.component';

@Component({
  selector: 'ts-order-completion',
  templateUrl: './order-completion.component.html',
  hostDirectives: [DataLoaderCaptureOverrideDirective],
  imports: [CommonModule, TranslationPipe, OrderDetailsComponent, RouterLink]
})
export class OrderCompletionComponent implements OnInit, OnDestroy {
  order_id: number;
  order_summary: OrderDetailsSummary;
  order_status_desc: string;
  asset_details: ExistingAssetDetails;
  private subscriptions: Subscription = new Subscription();

  constructor(
    private activated_route: ActivatedRoute,
    private shopping_svc: ShoppingService,
    private translation_svc: TranslationService
  ) {}

  ngOnInit(): void {
    this.subscriptions.add(
      this.activated_route.queryParamMap.subscribe(() => {
        this.order_id = this.activated_route.snapshot.params['id'];

        if (this.order_id) {
          this.subscriptions.add(
            this.shopping_svc.getOrderDetails(this.order_id).subscribe({
              next: (response: OrderDetailsSummary) => {
                this.order_summary = response;
                if (
                  this.order_summary.order_price_summary?.bulk_order_additional_info?.bulk_quantity
                )
                  this.order_summary.order_price_summary = calculateBulkOrderItems(
                    this.order_summary.order_price_summary
                  );

                this.asset_details = {
                  existing_asset: this.order_summary?.existing_asset!,
                  keep_plan: this.order_summary?.keep_plan,
                  existing_person: this.order_summary.source_order.existing_person,
                  replacement_user: this.order_summary.source_order.replacement_user
                };

                switch (this.order_summary.source_order.source_system_status) {
                  case 'Denied':
                    this.getDenialNotification();
                    break;
                  default:
                    this.getWhatNextNotification();
                    break;
                }
              },
              error: (error) => {}
            })
          );
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  get homeUrl(): string {
    return PageUrls.HOME;
  }

  get confirmationMsg(): string {
    return this.order_summary && this.order_summary.source_order.source_system_status !== 'Denied'
      ? this.translation_svc.translate('order_received')
      : this.translation_svc.translate('order_denied');
  }

  private getWhatNextNotification(): void {
    this.subscriptions.add(
      this.shopping_svc.getWhatNextNotification(this.order_id).subscribe({
        next: (response: NotificationResponse) => {
          this.order_status_desc = response.notifications[0]?.notification;
        },
        error: (error) => {}
      })
    );
  }

  private getDenialNotification(): void {
    this.subscriptions.add(
      this.shopping_svc.getDenialNotification(this.order_id).subscribe({
        next: (response: NotificationResponse) => {
          this.order_status_desc = response.notifications[0]?.notification;
        },
        error: (error) => {}
      })
    );
  }
}
